import clsx from "clsx";
import { RotateCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { CurvePhase, Logistics, LogisticsAssumptions, LogisticsMaintenance, MaintenanceFamily, MaintenancePhase, MaterielFamily } from "../../api/types";
import { PanelGroup } from "../../components/panel";
import { Motion } from "../../components/presence";
import { Badge, Button } from "../../components/ui";
import { formatNumber, k, t } from "../../i18n/i18n";
import { colorTone, CURVE_PHASES, dtoColor, dtoRequiredKey, dtoStartKey, familyLabel, FLEET_KEYS, percentText, REQUIRED_PHASES } from "../../maintenance";
import { designateDossierReadOnly, useDossierLocked, useWriteBlock } from "../../sharing/access";
import { AssumptionsBadge, customOf } from "../logistics/assumptions";
import { TONE_TEXT } from "./DtoCurve";

/** DTO de départ et DTO requise par parc (demande d'Alexis du 04/11/2026 : « l'utilisateur doit pouvoir faire varier
 *  la DTO requise par temps avec des sliders, par parc de matériels ») : un bloc par parc présent dans le dossier (nom
 *  complet, parc en véhicules), une ligne par temps — T0 « DTO de départ », T1 à T3 « DTO requise » —, chacune avec son
 *  curseur, sa valeur et, à côté, la DTO prévue du parc à ce temps, colorée face à la requise. Un curseur qui bouge se
 *  voit aussitôt (`Preview` : couleurs, paliers et courbe du parc, avant la réponse du serveur) ; il s'enregistre au
 *  relâcher, comme les autres hypothèses (mêmes contrôles entre postes, même lecture seule). La DTO de départ reste au
 *  moins égale à la requise en T1 : la valeur déplacée l'emporte, l'autre la suit (ici et sur le serveur). */

/** Curseurs en cours de réglage (hypothèse → valeur), pas encore rendus par le serveur. */
export type Preview = Partial<Record<keyof LogisticsAssumptions, number>>;

const ROW_LABELS: Record<CurvePhase, string> = {
  T0: k("DTO de départ"),
  T1: k("DTO requise"),
  T2: k("DTO requise"),
  T3: k("DTO requise"),
};

/** Colonnes du tableau : temps et rôle (« T0 · DTO de départ » sur une ligne), curseur, valeur réglée, DTO prévue. */
const COLUMNS = "grid grid-cols-[7.5rem_minmax(0,1fr)_2.5rem_3rem] gap-x-2";

const clamp = (v: number) => Math.max(0, Math.min(100, v));

/** Changements d'un curseur, la contrainte de départ ≥ requise en T1 comprise (comme le serveur). */
function changesOf(family: MaterielFamily, phase: CurvePhase, value: number, current: (key: keyof LogisticsAssumptions) => number): Preview {
  const start = dtoStartKey(family);
  const first = dtoRequiredKey(family, "T1");
  if (phase === "T0") return value < current(first) ? { [start]: value, [first]: value } : { [start]: value };
  const key = dtoRequiredKey(family, phase);
  if (phase === "T1" && value > current(start)) return { [key]: value, [start]: value };
  return { [key]: value };
}

/** Maintenance montrée avec les curseurs en cours de réglage : DTO requise et DTO de départ du réglage ; la courbe se
 *  décale d'autant que la DTO de départ (l'indisponibilité de départ n'est pas remise en état), couleurs, premier temps
 *  sous la requise et parc le plus en retard recalculés ; le serveur rend ensuite la valeur exacte. */
export function previewed(m: LogisticsMaintenance, assumptions: LogisticsAssumptions, preview: Preview): LogisticsMaintenance {
  if (!Object.keys(preview).length) return m;
  const families = m.families.map((f): MaintenanceFamily => {
    const startKey = dtoStartKey(f.family);
    const shift = (preview[startKey] ?? assumptions[startKey]) - assumptions[startKey];
    const required = Object.fromEntries(
      REQUIRED_PHASES.map((p) => [p, preview[dtoRequiredKey(f.family, p)] ?? f.required[p]]),
    ) as Record<MaintenancePhase, number>;
    const dto = Object.fromEntries(CURVE_PHASES.map((p) => [p, clamp(f.dto[p] + shift)])) as Record<CurvePhase, number>;
    const color = Object.fromEntries(CURVE_PHASES.map((p) => [p, p === "T0" ? null : dtoColor(dto[p], required[p])])) as MaintenanceFamily["color"];
    const below = REQUIRED_PHASES.find((p) => Math.round(dto[p]) < Math.round(required[p])) ?? null;
    return { ...f, start: f.start + shift, required, dto, color, below };
  });
  const phase = m.lowest?.phase;
  if (!phase) return { ...m, families };
  const lowest = families.reduce((a, b) => (b.dto[phase] - b.required[phase] < a.dto[phase] - a.required[phase] ? b : a));
  return {
    ...m,
    families,
    lowest: { family: lowest.family, phase, dto: lowest.dto[phase], color: lowest.color[phase], required: lowest.required[phase] },
  };
}

/** Une ligne : temps et rôle, curseur, valeur réglée, DTO prévue du parc à ce temps (colorée face à la requise).
 *  `value` : valeur enregistrée (comparée au relâcher) ; `shown` : valeur montrée, celle d'un autre curseur en cours
 *  de réglage qui la fait suivre (DTO de départ et DTO requise en T1). */
function FleetRow({
  family,
  phase,
  value,
  shown: previewValue,
  bounds,
  forecast,
  current: isCurrent,
  disabled,
  onPreview,
  onCommit,
}: {
  family: MaterielFamily;
  phase: CurvePhase;
  current: boolean;
  value: number;
  shown: number;
  bounds: { min: number; max: number; step: number };
  forecast: number;
  disabled: boolean;
  onPreview: (value: number) => void;
  onCommit: (value: number) => void;
}) {
  const locked = useDossierLocked();
  const [local, setLocal] = useState(previewValue);
  // Dernière valeur du curseur, lue au relâcher : un appui bref (saut sur la piste) relâche le curseur avant que React
  // n'ait rendu la nouvelle valeur.
  const latest = useRef(previewValue);
  // Valeur déjà envoyée : le relâcher, la touche et la perte du focus d'un même geste ne l'envoient qu'une fois.
  const sent = useRef<number | null>(null);
  useEffect(() => {
    setLocal(previewValue);
    latest.current = previewValue;
    sent.current = null;
  }, [previewValue]);
  const commit = () => {
    if (latest.current === value || latest.current === sent.current) return;
    sent.current = latest.current;
    onCommit(latest.current);
  };
  const required = phase === "T0" ? null : local;
  const color = dtoColor(forecast, required);
  const label = t("{family} · {role} en {phase}", { family: familyLabel(family), role: t(ROW_LABELS[phase]), phase });
  return (
    <div className={clsx(COLUMNS, "items-center py-1")}>
      <span className={clsx("text-meta leading-snug whitespace-nowrap", isCurrent ? "font-medium text-ink" : "text-ink-soft")}>
        <span className="font-semibold text-ink tabular-nums">{phase}</span> · {t(ROW_LABELS[phase])}
      </span>
      <input
        type="range"
        min={bounds.min}
        max={bounds.max}
        step={bounds.step}
        value={local}
        disabled={disabled}
        aria-label={label}
        aria-valuetext={percentText(local)}
        // Dossier en lecture seule pour le compte (docs/17 §3.2) : la valeur ne bouge pas, l'appui désigne le bandeau.
        aria-disabled={locked || undefined}
        onChange={(e) => {
          if (locked) return designateDossierReadOnly();
          const next = Number(e.target.value);
          latest.current = next;
          setLocal(next);
          onPreview(next);
        }}
        onPointerUp={commit}
        onKeyUp={commit}
        onBlur={commit}
        className="w-full min-w-0 accent-accent"
      />
      <span className="text-right text-meta font-semibold text-ink tabular-nums">{percentText(local)}</span>
      <span
        className={clsx("text-right text-meta tabular-nums transition-colors", TONE_TEXT[colorTone(color)])}
        title={phase === "T0" ? t("DTO de départ du parc") : t("DTO prévue du parc en {phase}", { phase })}
      >
        {percentText(forecast)}
      </span>
    </div>
  );
}

export function FleetTable({
  data,
  phase,
  preview,
  reverts,
  pending,
  onPreview,
  onCommit,
}: {
  data: Logistics;
  phase: MaintenancePhase;
  preview: Preview;
  reverts: number;
  pending: boolean;
  onPreview: (family: MaterielFamily, changes: Preview) => void;
  onCommit: (changes: Partial<LogisticsAssumptions>) => void;
}) {
  const writeBlock = useWriteBlock();
  const m = data.maintenance;
  if (!m || !m.families.length) return null;
  const shown = previewed(m, data.assumptions, preview);
  const custom = customOf(data, FLEET_KEYS);
  const current = (key: keyof LogisticsAssumptions) => preview[key] ?? data.assumptions[key];
  const defaults = Object.fromEntries(FLEET_KEYS.map((key) => [key, data.defaults[key]])) as Partial<LogisticsAssumptions>;
  return (
    <PanelGroup title={t("DTO de départ et DTO requise par parc")} actions={<AssumptionsBadge data={data} custom={custom} />}>
      <div className="px-4">
        <p className="text-meta leading-snug text-ink-soft">
          {t(
            "Pour chaque parc : la DTO de départ en T0, puis la DTO requise à la fin de T1, T2 et T3. À droite, la DTO prévue du parc, en couleur face à la requise ; la courbe suit aussitôt.",
          )}
        </p>
        <div aria-hidden className={clsx(COLUMNS, "mt-2 text-caps text-ink-muted")}>
          <span>{t("Temps")}</span>
          <span />
          <span className="text-right">{t("Réglée")}</span>
          <span className="text-right">{t("Prévue")}</span>
        </div>
        <div className="divide-y divide-line">
          {shown.families.map((f) => (
            <Motion key={f.family} origin="fade" className="py-2" data-maintenance-fleet={f.family}>
              <div role="group" aria-label={familyLabel(f.family)}>
                <div className="flex min-h-8 items-center gap-2">
                  <span className="min-w-0 flex-1 text-ui leading-snug font-medium text-ink">{familyLabel(f.family)}</span>
                  <span className="text-meta text-ink-muted tabular-nums">{t("{n} véhicules", { n: formatNumber(Math.round(f.vehicles)) })}</span>
                  {f.below && (
                    <Badge tone="warn" title={t("sous la DTO requise dès {phase}", { phase: f.below })}>
                      {t("Sous la requise")}
                    </Badge>
                  )}
                </div>
                {CURVE_PHASES.map((p) => {
                  const key = p === "T0" ? dtoStartKey(f.family) : dtoRequiredKey(f.family, p);
                  return (
                    <FleetRow
                      key={`${key}-${reverts}`}
                      family={f.family}
                      phase={p}
                      value={data.assumptions[key]}
                      shown={current(key)}
                      bounds={data.bounds[key]}
                      forecast={f.dto[p]}
                      current={p === phase}
                      disabled={pending}
                      onPreview={(value) => onPreview(f.family, changesOf(f.family, p, value, current))}
                      onCommit={(value) => onCommit(changesOf(f.family, p, value, current))}
                    />
                  );
                })}
              </div>
            </Motion>
          ))}
        </div>
        <p className="mt-1 text-meta leading-snug text-ink-muted">
          {t("La DTO de départ reste au moins égale à la DTO requise en T1. Réglages partagés par tous les postes du dossier.")}
        </p>
        <div className="py-2">
          <Button variant="secondary" size="sm" disabled={!custom || pending} onClick={() => onCommit(defaults)} {...writeBlock}>
            <RotateCcw size={14} /> {t("Revenir aux valeurs par défaut")}
          </Button>
        </div>
      </div>
    </PanelGroup>
  );
}
